/**
 * useTenantMutation - Hook factory for tenant-scoped API mutations
 *
 * Provides consistent mutation handling with:
 * - Automatic auth token and tenant ID injection
 * - Toast notifications for success/error
 * - Automatic query invalidation patterns
 * - Error parsing with parseApiError()
 *
 * @example
 * const createTask = useTenantMutation<Task, CreateTaskInput>(
 *   '/tasks',
 *   'POST',
 *   {
 *     onSuccess: () => {
 *       toast.success('Task created');
 *       queryClient.invalidateQueries({ queryKey: ['tasks'] });
 *     },
 *   }
 * );
 *
 * // Usage in component
 * createTask.mutate(taskData);
 */

import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { parseApiError, getErrorMessage } from '@/utils/api-errors';
import { toast } from '@/utils/toast';
import { useTenantStore } from '@/store/tenant.store';

type MutationMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface UseTenantMutationOptions<TData = unknown, TVariables = unknown> {
  /** Success callback after mutation completes */
  onSuccess?: (data: TData, variables: TVariables) => void;
  /** Error callback if mutation fails */
  onError?: (error: Error, variables: TVariables) => void;
  /** Callback before mutation starts */
  onMutate?: (variables: TVariables) => Promise<any> | any;
  /** Show success toast (default: false) */
  showSuccessToast?: boolean;
  /** Success toast message (default: 'Operation successful') */
  successMessage?: string;
  /** Show error toast (default: true) */
  showErrorToast?: boolean;
  /** Query keys to invalidate after successful mutation */
  invalidateKeys?: string[][];
  /** Additional React Query mutation options */
  mutationOptions?: Omit<UseMutationOptions<TData, Error, TVariables>, 'mutationFn' | 'onSuccess' | 'onError' | 'onMutate'>;
}

/**
 * Hook factory for tenant-scoped mutations (POST, PATCH, PUT, DELETE)
 *
 * @param endpoint - API endpoint (e.g., '/tasks')
 * @param method - HTTP method ('POST', 'PATCH', 'PUT', 'DELETE')
 * @param options - Optional configuration (callbacks, toast, invalidation)
 * @returns React Query mutation result
 *
 * @example
 * // Create mutation
 * const createIncident = useTenantMutation<Incident, CreateIncidentInput>(
 *   '/incidents',
 *   'POST',
 *   {
 *     showSuccessToast: true,
 *     successMessage: 'Incident created',
 *     invalidateKeys: [['incidents']],
 *   }
 * );
 *
 * @example
 * // Update mutation with custom ID in URL
 * const updateTask = useTenantMutation<Task, { id: string; data: UpdateTaskInput }>(
 *   '/tasks/:id',
 *   'PATCH',
 *   {
 *     showSuccessToast: true,
 *     successMessage: 'Task updated',
 *     invalidateKeys: [['tasks'], ['tasks', taskId]],
 *   }
 * );
 *
 * // Usage: updateTask.mutate({ id: '123', data: { title: 'New title' } });
 *
 * @example
 * // Delete mutation
 * const deleteContact = useTenantMutation<void, string>(
 *   '/contacts/:id',
 *   'DELETE',
 *   {
 *     showSuccessToast: true,
 *     successMessage: 'Contact deleted',
 *     invalidateKeys: [['contacts']],
 *   }
 * );
 *
 * // Usage: deleteContact.mutate(contactId);
 */
export function useTenantMutation<TData = unknown, TVariables = unknown>(
  endpoint: string,
  method: MutationMethod,
  options: UseTenantMutationOptions<TData, TVariables> = {}
) {
  const queryClient = useQueryClient();
  const { activeTenant } = useTenantStore();

  const {
    onSuccess,
    onError,
    onMutate,
    showSuccessToast = false,
    successMessage = 'Operation successful',
    showErrorToast = true,
    invalidateKeys = [],
    mutationOptions = {},
  } = options;

  return useMutation<TData, Error, TVariables>({
    mutationFn: async (variables: TVariables): Promise<TData> => {
      try {
        // Handle URL parameters (e.g., /tasks/:id)
        let url = endpoint;
        let requestData: any = variables;
        // A DELETE only carries a body when the caller opted in via the
        // { id, data } shape. Every other DELETE call site in the app is
        // bodyless today and must stay that way.
        let deleteCarriesBody = false;

        // If variables is an object with 'id' and 'data' properties, use them for URL and body
        if (
          typeof variables === 'object' &&
          variables !== null &&
          'id' in variables &&
          'data' in variables
        ) {
          const { id, data } = variables as { id: string; data: unknown };
          url = endpoint.replace(':id', id);
          requestData = data;
          deleteCarriesBody = true;
        }
        // If variables is a string (for DELETE), use it as ID
        else if (typeof variables === 'string' && endpoint.includes(':id')) {
          url = endpoint.replace(':id', variables);
          requestData = undefined;
        }

        // Execute the mutation based on method
        switch (method) {
          case 'POST':
            return await api.post<TData>(url, requestData);
          case 'PUT':
            return await api.put<TData>(url, requestData);
          case 'PATCH':
            return await api.patch<TData>(url, requestData);
          case 'DELETE':
            // Some DELETE routes mandate a body (e.g. the ICS-214 activity-entry
            // delete requires a `reason` for the legal audit-exception ledger).
            // Dropping `requestData` here made every such delete fail with a 400
            // that surfaced only as a generic validation toast.
            return await api.delete<TData>(url, deleteCarriesBody ? requestData : undefined);
          default:
            throw new Error(`Unsupported method: ${method}`);
        }
      } catch (error) {
        // Preserve structured fields from the API response (status, code,
        // and the per-field `details[]` array) so `onError` consumers can
        // surface field-level Zod validation messages inline (PEOPLE-002).
        // Re-throwing as `new Error(apiError.message)` would strip these.
        const apiError = parseApiError(error);
        const wrapped = new Error(apiError.message) as Error & {
          status?: number;
          code?: string;
          details?: typeof apiError.details;
        };
        if (apiError.status !== undefined) wrapped.status = apiError.status;
        if (apiError.code !== undefined) wrapped.code = apiError.code;
        if (apiError.details !== undefined) wrapped.details = apiError.details;
        throw wrapped;
      }
    },

    onSuccess: async (data, variables, context) => {
      // Invalidate specified query keys (prepend tenant ID to match useTenantQuery key format)
      if (invalidateKeys.length > 0) {
        const tenantId = activeTenant?.id;
        await Promise.all(
          invalidateKeys.map((queryKey) =>
            queryClient.invalidateQueries({
              queryKey: tenantId ? [tenantId, ...queryKey] : queryKey,
            })
          )
        );
      }

      // Show success toast if enabled
      if (showSuccessToast) {
        toast.success(successMessage);
      }

      // Call custom onSuccess callback
      if (onSuccess) {
        onSuccess(data, variables);
      }
    },

    onError: (error, variables, context) => {
      // Show error toast if enabled
      if (showErrorToast) {
        const parsed = parseApiError(error);
        const errorMessage = getErrorMessage(parsed);
        /*
         * F-070: error toasts persist to notification history at priority
         * "high" by default, so a 400 like "Claimant name: Required" filed a
         * high-priority notification indistinguishable from a failed dispatch —
         * a user fumbling a form three times filed three. The notification
         * centre is where an operator looks for things needing attention, and
         * routine validation feedback does not belong there.
         *
         * Split by status class rather than guessing from the message: a 4xx is
         * the caller's to fix, is already on screen, and is not actionable
         * later. A 5xx (or a network failure, which parses with no status) is
         * worth keeping, because the operator may only notice afterwards that
         * the write never landed.
         */
        const isClientError = typeof parsed.status === 'number' && parsed.status >= 400 && parsed.status < 500;
        toast.error(errorMessage, undefined, undefined, { persist: !isClientError });
      }

      // Call custom onError callback
      if (onError) {
        onError(error, variables);
      }
    },

    onMutate,
    ...mutationOptions,
  });
}
